import { useState, useMemo } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SectionHeaderWithControls } from '@components/SectionHeaderWithControls';
import { EmptyState } from '@components/EmptyState';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { useChargesDeletion } from '../../hooks/useChargesDeletion';
import { DeleteChargeModal } from '../DeleteCharge';
import { AddChargeModal } from '../AddCharge';
import { ChargeCard } from './ChargeCard';
import { formatCurrency } from '@utils/numberFormatters';
import type {
  WorkOrderCharge,
  ChargesListProps
} from '../../types/charges.types';

type Props = ChargesListProps & {
  charges?: WorkOrderCharge[];
  isLoading?: boolean;
  jobNumber: string;
};

export const ChargesList = ({
  charges = [],
  isLoading = false,
  jobNumber,
  isReadOnly = false
}: Props) => {
  const { t } = useTranslation('charges');
  const { isMobile } = useResponsive();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const totalChargesAmount = useMemo(() => {
    return charges.reduce((sum, charge) => {
      const quantity = charge.lineQuantity || 0;
      const unitCost = charge.lineCost || 0;
      const totalCost = charge.lineTotalCost || unitCost * quantity;
      return sum + totalCost;
    }, 0);
  }, [charges]);

  const {
    isDeleteModalOpen,
    chargeToDelete,
    isDeleting,
    handleDeleteCharge,
    handleConfirmDelete,
    handleCloseModal
  } = useChargesDeletion(jobNumber);

  const handleOpenAddModal = () => {
    if (isReadOnly) return;
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: isMobile ? 200 : 300
          }}
        >
          <SectionHeaderWithControls
            title={t('title')}
            count={charges.length}
            canCreate={false}
            horizontalPadding="0px"
            pb={1}
            button={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {charges.length > 0 && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main'
                    }}
                  >
                    Total: {formatCurrency(totalChargesAmount)}
                  </Typography>
                )}
                {!isReadOnly && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddModal}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    {t('addCharge')}
                  </Button>
                )}
              </Box>
            }
          >
            <></>
          </SectionHeaderWithControls>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 1 : 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              flexGrow: 1,
              px: isMobile ? 1 : 0
            }}
          >
            {charges.length > 0 ? (
              charges.map((charge, index) => (
                <ChargeCard
                  key={`${charge.sequenceNumber || index}`}
                  charge={charge}
                  onDelete={!isReadOnly ? handleDeleteCharge : undefined}
                />
              ))
            ) : (
              <Box sx={{ mt: 4 }}>
                <EmptyState
                  message={t('noCharges')}
                  minHeight={isMobile ? 150 : 250}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <AddChargeModal
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        jobNumber={jobNumber}
      />

      <DeleteChargeModal
        open={isDeleteModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        chargeType={chargeToDelete?.chargeType || 'this charge'}
        isDeleting={isDeleting}
      />
    </>
  );
};
